import {
  CalcExpression,
  CalcExpressionInput,
  DatasetSource,
  GalaxyKPIDrivers,
  HctrKPIDrivers,
  KPIDrivers,
} from '@services/api/deckManagement/graphql/schema/API';
import { HeaderTreePageLayout } from 'types/Page.types';
import { DriverDisplayOptionEntry, DriverDisplayOptionMap } from 'types/PageContext.types';

/**
 * Union of every KPI dimension field name across all dataset shapes (Galaxy + HCTR). Excludes the
 * discriminator fields `__typename` and `datasetSource` — those identify the dataset, they are not
 * driver dimensions. Matches the widened accessor types on {@link KpiDimensionConfig}.
 */
export type KpiDriverField =
  | keyof Omit<GalaxyKPIDrivers, '__typename' | 'datasetSource'>
  | keyof Omit<HctrKPIDrivers, '__typename' | 'datasetSource'>;

/**
 * A flat, dataset-agnostic bag of KPI driver values, keyed by {@link KpiDriverField}.
 *
 * Carries NO `__typename`. This is the shape for the write/edit path (dimension-picker edits, variant
 * override updates): a `KpiDriverValues` is assignable to `Partial<GalaxyKPIDrivers>`, so it flows
 * through `onChange` into the still-Galaxy-only mutation layer without a cast.
 */
export type KpiDriverValues = Partial<Record<KpiDriverField, string | null>>;

/**
 * A {@link KpiDriverValues} bag that also carries the wire dataset discriminators.
 *
 * This is the shape for the read path (values coming off the wire, placeholder drivers, the input to
 * `generateDriverDisplayOptions`): `datasetSource` (and the concrete `__typename`) let consumers
 * resolve the {@link DatasetSource} from the object itself rather than being told it out of band.
 * Assignable from both {@link GalaxyKPIDrivers} and {@link HctrKPIDrivers}.
 */
export type AnyKPIDrivers = KpiDriverValues & {
  // Concrete implementer typenames plus the `KPIDrivers` interface typename, so a value typed as the
  // interface (e.g. a model's `deckline_drivers` / `page_drivers`) is assignable here.
  __typename?: GalaxyKPIDrivers['__typename'] | HctrKPIDrivers['__typename'] | KPIDrivers['__typename'];
  datasetSource?: DatasetSource | null;
};

/**
 * Interface for defining Segment display data in the Page and Deckline Drivers Side Panel
 */
export interface SegmentDisplayData {
  kpiDimension: KpiDimension;
  value: string;
  hierarchy?: string | null;
  active: boolean;
}

/**
 * Driver types for page configuration in the side panel
 */
export enum DriverType {
  PAGE = 'page',
  DECKLINE = 'deckline',
  // For the `denominator_kpi_id_for_pct` field(s)
  DENOMINATOR_FOR_PERCENT = 'denominator_for_percent',
}

/**
 * The side panel's DEV_TEAM_ADMIN-only "Admin" tab. Kept out of {@link DriverType} — it is a panel
 * tab, not a driver set — so driver logic never has to reason about a non-driver value.
 */
export const ADMIN_TAB = 'admin';

/**
 * A selectable side panel tab: any {@link DriverType} driver tab, or the Admin tab. Narrow with
 * `tab === ADMIN_TAB` to recover a plain {@link DriverType} for the driver-editing paths.
 */
export type PanelTab = DriverType | typeof ADMIN_TAB;

/**
 * Page-layout preview overrides an admin can pin (all optional). Map to the `getPageLayout`
 * `deck_cycle` / `publish_year` / `as_of_date` arguments. Undefined = not pinned (server uses the
 * deck's own anchor). For validation/authoring preview only — NOT a render path (a pinned anchor can
 * describe a period the BI pipeline has not materialised values for, so header and numbers disagree).
 */
export interface PageLayoutOverrides {
  deckCycle?: string;
  publishYear?: string;
  asOfDate?: string;
}

/**
 * Page Admin Panel Props
 */
export interface PageAdminPanelProps {
  /**
   * The currently pinned preview overrides (empty object = none pinned).
   */
  overrides: PageLayoutOverrides;
  /**
   * Called with the next overrides when the admin changes a field.
   */
  onOverridesChange: (overrides: PageLayoutOverrides) => void;
}

/**
 * Dropdown options for the side panel
 */
export enum PageSidePanelDropDown {
  NONE = 'N/A',
  BLANK_LINE = 'Blank Line',
  ADJUSTMENT = 'Adjustment',
  CALCULATED_ADJUSTMENT = 'Calculated Adjustment',
  BASE_KPI = 'Base KPI',
  CALCULATED_KPI = 'Calculated KPI',
  SUBTOTAL = 'Subtotal',
}

/**
 * The field types that are supported for given 'dimensions' on the side panel.
 * Most are DimensionPicker, but some can be other field types (such as Reporting Currency).
 * @see {@link KPI_DIMENSION_CONFIG_MAP} for implementation usage.
 */
export enum BaseKpiDimensionFieldTypes {
  /**
   * For KPI Dimensions supported by the DimensionPicker.
   * @see BaseKpiDimensionFieldTypeDimensionPicker
   */
  DimensionPicker = 'DimensionPicker',
  /**
   * For KPI Dimensions where there is a fixed list of available options.
   * @see BaseKpiDimensionFieldTypeSelect
   */
  Select = 'Select',
  /**
   * For KPI Dimensions when it is not configurable by users (such as Reporting Currency)
   * @see BaseKpiDimensionFieldTypeReadOnly
   */
  ReadOnly = 'ReadOnly',
}

/**
 * DimensionPicker field type configuration
 */
export interface BaseKpiDimensionFieldTypeDimensionPicker {
  type: BaseKpiDimensionFieldTypes.DimensionPicker;
  dimensionName: string;
  /**
   * Optional. When provided, used as the filter for the root nodes in the DimensionPicker.
   * When not provided, all root nodes are shown.
   * When provided as an empty array, no root nodes are shown.
   */
  rootNodesFilter?: string[] | null;
}

/**
 * Options for Select box field type configuration
 */
export interface BaseKpiDimensionFieldTypeSelectOption {
  /**
   * Optional. When provided, used as the display text. When not provided, the value is also used as the label.
   */
  label?: string;
  /**
   * The value to be used for the field.
   */
  value: string;
}

/**
 * Select box field type configuration
 */
export interface BaseKpiDimensionFieldTypeSelect {
  type: BaseKpiDimensionFieldTypes.Select;
  options: BaseKpiDimensionFieldTypeSelectOption[];
  /**
   * Which value (from `options`) is considered the root - meaning that it does not need to be included in KPI IDs.
   */
  rootValue: string;
}

/**
 * ReadOnly field type configuration
 */
export interface BaseKpiDimensionFieldTypeReadOnly {
  type: BaseKpiDimensionFieldTypes.ReadOnly;
  // When provided, this value should always be used for the DimensionField. (i.e.: USD for Reporting Currency)
  staticValue?: string;
}

/**
 * Union Field type configuration for Deckline Dimensions
 */
export type BaseKpiDimensionFieldType =
  BaseKpiDimensionFieldTypeDimensionPicker | BaseKpiDimensionFieldTypeSelect | BaseKpiDimensionFieldTypeReadOnly;

/**
 * Deckline Dimension Configuration
 */
export interface KpiDimensionConfig<TDimension = KpiDimension> {
  key: TDimension;
  label: string;
  // The key formatted as it is seen in a KPI ID / Formula. (e.g.: revgp is 'REVGP', account is 'Account')
  formulaKey?: string;
  baseKpiField: BaseKpiDimensionFieldType;
  decklineDrivers?: {
    hierarchyAccessor?: KpiDriverField;
    valueAccessor: KpiDriverField;
  };
}
/**
 * Available Dimensions for KPI definitions
 * The values here match the properties found in {@link GalaxyKPIDrivers} and {@link HctrKPIDrivers} from the API.
 */
export enum KpiDimension {
  // GALAXY
  ACCOUNT = 'account',
  LOCATION = 'location',
  REVGP = 'revgp',
  COST_CENTER = 'costcenter',
  COMPANY = 'company',
  PROJECT = 'project',
  REPORTING_CURRENCY = 'reporting_currency',
  CHANNEL = 'channel',
  PRODUCT = 'product',
  COST_TYPE = 'costtype',
  COST_POOL = 'costpool',
  DATA_SOURCE = 'datasource',
  FIELD_TYPE = 'fieldtype',

  // HCTR-specific
  MEASURE = 'measure',
  TRANSFER_DIRECTION = 'transferDirection',
  WORKFLOW = 'workflow',
}

/**
 * Generic Dimension Mapping Configuration
 */
export type DimensionConfigMap<T extends string | number | symbol> = { [key in T]: KpiDimensionConfig<T> };

/**
 * Deckline Dimension Mapping Configuration
 */
export type KpiDimensionConfigMap = DimensionConfigMap<KpiDimension>;

/**
 * Base KPI Panel Props
 */
export interface BaseKpiPanelProps {
  /**
   * The segment/dimension display data
   */
  segmentDisplayData: DriverDisplayOptionMap;
  /**
   * Whether editing is disabled due to deck lock
   */
  isEditingDisabled?: boolean;
  /**
   * Triggered when customer clicks on the toggle switch for a given dimension segment
   * @param index - the index of the segment in the segmentDisplayData array
   * @param checked - the new value for the switch state
   */
  onToggleSwitch?: (key: KpiDimension, checked: boolean) => void;
  /**
   * Triggered when customer clicks on the edit button for a given dimension segment
   * @param index - the index of the segment in the segmentDisplayData array
   * @param segmentData - the segment data for the given index
   */
  onEditStart?: (key: KpiDimension, segmentData: DriverDisplayOptionEntry) => void;
  /**
   * Triggered when customer confirms a change for a given dimension segment
   * @param index - the index of the segment in the segmentDisplayData array
   * @param segmentData - the segment data for the given index
   * @param updates - the KPI drivers values which have changed.
   */
  onChange?: (
    key: KpiDimension,
    segmentData: DriverDisplayOptionEntry,
    updates: Partial<GalaxyKPIDrivers>,
    newKpiId: string,
  ) => void;
}

/**
 * Calculated KPI Panel Props
 */
export interface CalculatedKPIPanelProps {
  onFormulaChange?: (newId: string, newFormula: string) => void;
  /**
   * Fires with the authored expression (serialized `calc_members`) whenever the builder's segments or
   * the chosen pins change. Replaces {@link onFormulaChange} for the calc flow: the panel now persists
   * `calc_members` and lets the backend derive the per-column `kpi_id`/`kpi_formula`.
   * See `design-docs/page-editor/calculated-kpis.md` (Data model — the wire format).
   */
  onCalcMembersChange?: (calcMembers: CalcExpressionInput) => void;
  /**
   * The served `calc_members` for the row, when present. Non-null → render the expression read-only
   * from these tokens; null/undefined → the legacy raw-id read-only path.
   */
  calcMembers?: CalcExpression | null;
  /**
   * The page layout (from `useGetPageLayout`), used to compute the column-owned dimension set so the
   * panel can exclude column-owned dimensions from the editable segment matrix.
   */
  pageLayout?: HeaderTreePageLayout | null;
  /**
   * The initial formula that will be used to render the state of the Calculated KPI Panel
   */
  initialFormula?: string;

  initialKpiId?: string | null;

  pageDrivers: DriverDisplayOptionMap;

  /**
   * Which dataset's dimension render list the panel iterates (GALAXY vs HCTR dimensions). A Calc-KPI
   * row's dataset is the page's — resolved by the caller from `page_drivers`. Defaults to GALAXY to
   * preserve legacy single-dataset behavior when a caller does not pass it.
   */
  datasetSource?: DatasetSource;

  /**
   * When provided, ensures the KpiDimensions are always included in the generated formulas (even if they are rootNodes)
   */
  alwaysInclude?: string[];

  disabled?: boolean;
}

/**
 * Page Denominator Panel Props
 */
export interface PageDenominatorPanelProps {
  /**
   * Whether editing is disabled due to deck lock
   */
  isEditingDisabled?: boolean;
}

/**
 * Selector value for the Page Settings view's denominator option (as opposed to a {@link DatasetSource}).
 */
export const PAGE_SETTINGS_DENOMINATOR_VIEW = 'DENOMINATOR';

/**
 * A Page Settings view option: one of the page's configured datasets, or its denominator drivers.
 */
export type PageSettingsView = DatasetSource | typeof PAGE_SETTINGS_DENOMINATOR_VIEW;

/**
 * Page Settings Panel Props
 */
export interface PageSettingsPanelProps {
  /**
   * The selected Page Settings view; falls back to the first available option when null or not offered.
   * Owned by the parent so the selection survives the panel unmounting on driver tab switches.
   */
  selectedView: PageSettingsView | null;
  /**
   * Called with the new view when the user changes the Page Settings selection
   */
  onViewChange: (view: PageSettingsView) => void;
}
